
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

export const useAmbassadorStats = () => {
  const [referralCount, setReferralCount] = useState<number>(0);
  const [ambassadorRank, setAmbassadorRank] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchReferralCount = async (userId: string) => {
    try {
      // Get the user's referral code
      const { data: profile } = await supabase
        .from('profiles')
        .select('referral_code')
        .eq('id', userId)
        .single();

      if (!profile?.referral_code) {
        setReferralCount(0);
        return 0;
      }

      // Count profiles that were referred by this code
      const { count, error: countError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('referred_by', profile.referral_code);

      if (countError) throw countError;

      const currentCount = count || 0;
      setReferralCount(currentCount);
      
      if (currentCount >= 1) {
        setAmbassadorRank(`Ambassador Rank ${currentCount}`);
      } else {
        setAmbassadorRank(null);
      }

      return currentCount;
    } catch (error: any) {
      console.error('Error in fetchReferralCount:', error.message);
      throw error;
    }
  };

  useEffect(() => {
    const initializeStats = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw new Error('Authentication error. Please sign in again.');
        }
        
        if (!session) {
          toast({
            title: "Session expired",
            description: "Please sign in again",
            variant: "destructive",
          });
          navigate('/auth');
          return;
        }

        await fetchReferralCount(session.user.id);
      } catch (error: any) {
        console.error('Error fetching ambassador stats:', error.message);
        setError(error.message);
        
        if (error.message.includes('Authentication') || error.message.includes('session')) {
          toast({
            title: "Authentication error",
            description: "Please sign in again",
            variant: "destructive",
          });
          navigate('/auth');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeStats();
  }, [navigate, toast]);

  return {
    referralCount,
    ambassadorRank,
    isLoading,
    error
  };
};
